import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { createMarkdownComponents } from './common/MarkdownComponents';
import { getCodeSnippet } from '../api/images';

export const CodePopup = ({ isOpen, onClose, snippetName, dynamicData = {} }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Use the same dark mode detection as documentation
  const isDarkMode = document.documentElement.classList.contains('dark');

  useEffect(() => {
    if (isOpen && snippetName) {
      fetchSnippet();
    }
  }, [isOpen, snippetName]);

  const fetchSnippet = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCodeSnippet(snippetName);
      // Replace placeholders with dynamic data
      let processedContent = data.content || data;

      // Replace {{variable}} placeholders with actual values
      Object.keys(dynamicData).forEach(key => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedContent = processedContent.replace(regex, dynamicData[key]);
      });

      setContent(processedContent);
    } catch (err) {
      console.error('Error fetching code snippet:', err);
      setError('Failed to load code snippet');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    // Extract code blocks from markdown
    const codeBlockRegex = /```[\s\S]*?```/g;
    const codeBlocks = content.match(codeBlockRegex);

    if (codeBlocks) {
      // Remove the ``` markers and join all code blocks
      const code = codeBlocks
        .map(block => block.replace(/```\w*\n?/g, '').replace(/```$/g, ''))
        .join('\n\n');

      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Fallback to copying all content
      navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  // Create markdown components (same as documentation)
  const markdownComponents = createMarkdownComponents(isDarkMode);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      onClick={onClose}
    >
      <div
        className="bg-ui-light-card/95 dark:bg-ui-dark-card/95 backdrop-blur-sm rounded-xl shadow-2xl w-full overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxWidth: '900px', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-ui-light-card/98 dark:bg-ui-dark-card/98 backdrop-blur-md border-b border-ui-light-border/50 dark:border-ui-dark-border/50 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-100/80 dark:bg-primary-900/30">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-600 dark:text-primary-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Python Code Example
            </h3>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              disabled={loading || error}
              className="px-3 py-1.5 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:text-primary-600 dark:hover:text-primary-400 bg-ui-light-hover/80 dark:bg-ui-dark-hover/80 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy Code
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover/80 dark:hover:bg-ui-dark-hover/80 hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-auto p-6" style={{ maxHeight: 'calc(85vh - 80px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-status-error-light-text dark:text-status-error-dark-text mb-2">{error}</p>
              <button
                onClick={fetchSnippet}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <div className="max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}

          {!loading && !error && (
            <div className="mt-6 pt-4 border-t border-ui-light-border/50 dark:border-ui-dark-border/50">
              <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Learn more at:{' '}
                <a
                  href="https://github.com/schwarzam/adss"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  github.com/schwarzam/adss
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodePopup;
